'use client';

import React, { useEffect, useState } from 'react';
import { Heart, Copy, CheckCircle2, ExternalLink, AlertTriangle, Loader2 } from 'lucide-react';
import { getPublicDonationSettings, type DonationSettings } from '@/lib/api-client';
import { useSite } from '@/context/SiteContext';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: do nothing
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-card-hover-bg hover:bg-border-default border border-default text-slate-300 hover:text-white text-xs font-medium rounded-lg transition-colors"
    >
      {copied ? <CheckCircle2 size={13} className="text-emerald-400" /> : <Copy size={13} />}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

export default function DonatePage() {
  const { siteName } = useSite();
  const [settings, setSettings] = useState<DonationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getPublicDonationSettings()
      .then((data) => {
        setSettings(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Unable to load donation information. Please try again later.');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="bg-surface flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-sky-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-surface flex items-center justify-center px-4 py-20">
        <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 text-amber-400 px-5 py-4 rounded-xl text-sm max-w-md">
          <AlertTriangle size={18} className="flex-shrink-0" />
          {error}
        </div>
      </div>
    );
  }

  if (!settings || !settings.enabled) {
    return (
      <div className="bg-surface flex items-center justify-center px-4 py-20">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-card-bg border border-default flex items-center justify-center mx-auto mb-4">
            <Heart size={24} className="text-fg-subtle" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Donations not available</h1>
          <p className="text-fg-subtle text-sm">Donations are not currently enabled. Check back later.</p>
        </div>
      </div>
    );
  }

  const hasPayPal = !!settings.paypalLink;
  const hasZelle = !!settings.zelleEmail;
  const hasVenmo = !!settings.venmoHandle;
  const hasAnyMethod = hasPayPal || hasZelle || hasVenmo;

  return (
    <div className="bg-surface py-16 px-4">
      <div className="max-w-lg mx-auto">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500/20 to-pink-500/10 border border-rose-500/20 flex items-center justify-center mx-auto mb-5">
            <Heart size={28} className="text-rose-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Support {siteName}</h1>
          <p className="text-fg-muted text-sm leading-relaxed max-w-sm mx-auto">
            {`We're a small team working to keep ${siteName} free for everyone. If you find value in what we're building, any contribution helps us cover server costs and keep the lights on.`}
          </p>
        </div>

        {/* Custom message */}
        {settings.message && (
          <div className="bg-card-bg border border-default rounded-xl px-5 py-4 mb-6 text-slate-300 text-sm leading-relaxed">
            {settings.message}
          </div>
        )}

        {/* Payment methods */}
        {hasAnyMethod ? (
          <div className="space-y-4">

            {/* PayPal */}
            {hasPayPal && (
              <div className="bg-card-bg border border-default rounded-xl p-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-[#003087]/20 border border-[#003087]/30 flex items-center justify-center flex-shrink-0">
                      {/* PayPal icon (simplified) */}
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[#009cde]">
                        <path d="M7.144 19.532l1.049-5.751c.11-.606.645-1.043 1.26-1.043h1.417c3.915 0 6.73-1.733 7.595-4.805.089-.32.159-.638.213-.954C18.28 6.98 18 7 17.697 7H9.001c-.614 0-1.149.437-1.26 1.043L5.002 19.532H7.144z" fill="currentColor" opacity=".5"/>
                        <path d="M18.677 6.979c-.054.316-.124.634-.213.954-.865 3.072-3.68 4.805-7.595 4.805H9.452c-.614 0-1.15.437-1.26 1.043l-1.05 5.751H5.003l1.739-9.51A1.282 1.282 0 018 9h9.697c.303 0 .583-.02.98.02z" fill="currentColor"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">PayPal</div>
                      <div className="text-xs text-fg-subtle mt-0.5">Click to donate via PayPal</div>
                    </div>
                  </div>
                  <a
                    href={settings.paypalLink.startsWith('http') ? settings.paypalLink : `https://${settings.paypalLink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-4 py-2 bg-[#009cde] hover:bg-[#007bb5] text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    Donate
                    <ExternalLink size={13} />
                  </a>
                </div>
              </div>
            )}

            {/* Zelle */}
            {hasZelle && (
              <div className="bg-card-bg border border-default rounded-xl p-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-400 font-bold text-sm">Z</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white">Zelle</div>
                      <div className="text-xs text-fg-muted mt-0.5 truncate font-mono">{settings.zelleEmail}</div>
                    </div>
                  </div>
                  <CopyButton text={settings.zelleEmail} />
                </div>
              </div>
            )}

            {/* Venmo */}
            {hasVenmo && (
              <div className="bg-card-bg border border-default rounded-xl p-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-sky-400 font-bold text-sm">V</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white">Venmo</div>
                      <div className="text-xs text-fg-muted mt-0.5 truncate font-mono">{settings.venmoHandle}</div>
                    </div>
                  </div>
                  <CopyButton text={settings.venmoHandle} />
                </div>
              </div>
            )}

          </div>
        ) : (
          <div className="text-center text-fg-subtle text-sm py-8 bg-card-bg border border-default rounded-xl">
            No payment methods configured yet. Check back soon.
          </div>
        )}

        {/* Thank you note */}
        <p className="text-center text-slate-600 text-xs mt-8">
          Thank you for your support — it means the world to us.
        </p>
      </div>
    </div>
  );
}
